import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { Inbox as InboxIcon, Send } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import api from "../lib/api";

const SUBJECTS = ["General question", "About a booking", "Scheduling change", "Something else"];

const inputCls =
  "w-full rounded-2xl border border-line bg-white px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none";

const timeLabel = (iso) => {
  try {
    return format(new Date(iso), "MMM d, h:mm a");
  } catch {
    return "";
  }
};

const ComposeForm = ({ prefillRef }) => {
  const [form, setForm] = useState({ owner_name: "", owner_email: "", subject: SUBJECTS[0], booking_ref: prefillRef || "", body: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefillRef) setForm((f) => ({ ...f, booking_ref: prefillRef }));
  }, [prefillRef]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/messages", form);
      setDone(true);
      toast.success("Sent straight to Ari's inbox.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send — please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-line bg-white p-10 text-center" data-testid="msg-compose-success">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-marmalade/10">
          <Send className="h-6 w-6 text-marmalade" />
        </span>
        <h3 className="mt-6 font-display text-3xl font-medium text-ink">Winged on its way.</h3>
        <p className="mt-3 max-w-xs text-sm leading-relaxed text-inkSoft">
          Your message is in Ari's private inbox. Check for her reply below — just come back and enter your email.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid="msg-compose-again"
          className="mt-6 rounded-full border border-ink/15 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors duration-200 hover:bg-sand"
        >
          Write another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-line bg-white p-8 md:p-10" data-testid="msg-compose-form">
      <h3 className="font-display text-3xl font-medium text-ink">Write to Ari</h3>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input
          value={form.owner_name}
          onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))}
          placeholder="Your name"
          data-testid="msg-name"
          className={inputCls}
          required
        />
        <input
          type="email"
          value={form.owner_email}
          onChange={(e) => setForm((f) => ({ ...f, owner_email: e.target.value }))}
          placeholder="Your email"
          data-testid="msg-email"
          className={inputCls}
          required
        />
      </div>
      <select
        value={form.subject}
        onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
        data-testid="msg-subject"
        className={`${inputCls} mt-4`}
      >
        {SUBJECTS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <input
        value={form.booking_ref}
        onChange={(e) => setForm((f) => ({ ...f, booking_ref: e.target.value.toUpperCase() }))}
        placeholder="Booking reference (optional — e.g. PL-XXXXXX)"
        data-testid="msg-ref"
        className={`${inputCls} mt-4 font-mono tracking-wider`}
      />
      <textarea
        value={form.body}
        onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
        rows={5}
        placeholder="Hi Ari — I was wondering…"
        data-testid="msg-body"
        className={`${inputCls} mt-4 resize-none leading-relaxed`}
        required
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="msg-send-btn"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-marmalade py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-marmalade-deep disabled:opacity-60"
      >
        {sending ? "Sending…" : "Send to Ari"}
        <Send className="h-4 w-4" />
      </button>
    </form>
  );
};

const ThreadView = ({ entry, onReply }) => {
  const { thread, booking_status } = entry;
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await onReply(thread.id, reply.trim());
      setReply("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full flex-col rounded-3xl border border-line bg-white p-6 md:p-8" data-testid={`msg-thread-${thread.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4">
        <div>
          <p className="font-display text-2xl font-medium text-ink">{thread.subject}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft">
            {thread.owner_name} · {thread.owner_email}
            {thread.booking_ref ? ` · ${thread.booking_ref}` : ""}
          </p>
        </div>
        {booking_status && (
          <span
            className={`rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${
              booking_status === "approved"
                ? "border-forest/25 bg-forest/10 text-forest"
                : booking_status === "declined"
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-line bg-sand text-ink"
            }`}
            data-testid="msg-booking-status"
          >
            Request {booking_status}
          </span>
        )}
      </div>
      <div className="mt-5 flex-1 space-y-4 overflow-y-auto pr-1" style={{ maxHeight: 420 }}>
        {thread.messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "ari" ? "justify-start" : "justify-end"}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed ${
                m.sender === "ari" ? "bg-forest text-cream" : "bg-sand text-ink"
              }`}
              data-testid={m.sender === "ari" ? "msg-ari-bubble" : "msg-owner-bubble"}
            >
              <p>{m.body}</p>
              <p className={`mt-2 font-mono text-[9px] uppercase tracking-[0.16em] ${m.sender === "ari" ? "text-cream/60" : "text-inkSoft"}`}>
                {m.sender === "ari" ? "Ari" : thread.owner_name} · {timeLabel(m.created_at)}
              </p>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="mt-5 flex gap-3 border-t border-line pt-5">
        <input
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write a reply…"
          data-testid="msg-reply-input"
          className={`${inputCls} flex-1`}
        />
        <button
          type="submit"
          disabled={sending || !reply.trim()}
          data-testid="msg-reply-send"
          className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          Reply
        </button>
      </form>
    </div>
  );
};

const Messages = () => {
  const [searchParams] = useSearchParams();
  const [lookupEmail, setLookupEmail] = useState("");
  const [threads, setThreads] = useState(null);
  const [selected, setSelected] = useState(0);
  const [lookingUp, setLookingUp] = useState(false);

  const lookup = async (email) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter the email you wrote from.");
      return;
    }
    setLookingUp(true);
    try {
      const { data } = await api.get(`/messages/lookup?email=${encodeURIComponent(email)}`);
      setThreads(data);
      setSelected(0);
      if (data.length === 0) toast.info("No messages found for that email yet.");
    } catch {
      toast.error("Could not check your inbox — please try again.");
    } finally {
      setLookingUp(false);
    }
  };

  const reply = async (threadId, body) => {
    try {
      await api.post("/messages/reply", { thread_id: threadId, email: lookupEmail, body });
      toast.success("Reply sent to Ari.");
      await lookup(lookupEmail);
    } catch {
      toast.error("Could not send your reply — please try again.");
    }
  };

  const entries = threads || [];

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="pt-28 md:pt-36">
        <div className="mx-auto max-w-[1240px] px-6 pb-28 md:px-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade">Private inbox</p>
          <h1 className="mt-4 font-display text-5xl font-medium leading-[0.98] tracking-tight text-ink md:text-7xl">
            <MaskedLines lines={[<span key="a">Message</span>, <span key="b">
              <em className="text-marmalade">Ari</em> directly.</span>]} />
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-inkSoft md:text-lg">
            Questions, updates, special requests — straight to Ari's private inbox, no phone tag. Her replies
            land here too; just check with your email.
          </p>

          <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <ComposeForm prefillRef={searchParams.get("ref") || ""} />
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="flex h-full flex-col rounded-3xl border border-line bg-sand/50 p-6 md:p-8" data-testid="msg-inbox-panel">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white">
                    <InboxIcon className="h-5 w-5 text-forest" />
                  </span>
                  <div>
                    <h3 className="font-display text-2xl font-medium text-ink">Check your replies</h3>
                    <p className="text-xs text-inkSoft">Enter the email you wrote from.</p>
                  </div>
                </div>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <input
                    type="email"
                    value={lookupEmail}
                    onChange={(e) => setLookupEmail(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="msg-lookup-email"
                    className={inputCls}
                  />
                  <button
                    onClick={() => lookup(lookupEmail)}
                    disabled={lookingUp}
                    data-testid="msg-lookup-btn"
                    className="rounded-full bg-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-forest disabled:opacity-60"
                  >
                    {lookingUp ? "Checking…" : "Open inbox"}
                  </button>
                </div>

                {threads !== null && entries.length === 0 && (
                  <p className="mt-8 rounded-2xl bg-white p-6 text-sm text-inkSoft" data-testid="msg-inbox-empty">
                    Nothing here yet — write to Ari on the left and her replies will appear in this inbox.
                  </p>
                )}

                {entries.length > 0 && (
                  <div className="mt-6 flex flex-1 flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                      {entries.map((t, i) => (
                        <button
                          key={t.thread.id}
                          onClick={() => setSelected(i)}
                          data-testid={`msg-thread-tab-${t.thread.id}`}
                          className={`rounded-full border px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-200 ${
                            selected === i ? "border-ink bg-ink text-cream" : "border-line bg-white text-inkSoft hover:text-ink"
                          }`}
                        >
                          {t.thread.subject}
                        </button>
                      ))}
                    </div>
                    <ThreadView entry={entries[selected]} onReply={reply} />
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Messages;
